import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  archiveFeatureFlag,
  describeError,
  getFeatureFlagCatalog,
  listFeatureFlags,
  type FeatureFlagCatalogItem,
  type FeatureFlagDto,
  type FeatureFlagScope,
} from '@/lib/api';
import { describeFeatureFlagError } from '@/lib/feature-flag-errors';
import { FeatureFlagCreateModal, type FlagPrefill } from '@/pages/FeatureFlagCreateModal';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { KillSwitchBadge } from '@/components/KillSwitchBadge';

type StatusFilter = 'Active' | 'Archived' | '';

const SCOPE_OPTIONS: Array<FeatureFlagScope | ''> = ['', 'Global', 'PerTenant'];
const STATUS_OPTIONS: StatusFilter[] = ['', 'Active', 'Archived'];

export function FeatureFlagsListPage() {
  const [items, setItems] = useState<FeatureFlagDto[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [scope, setScope] = useState<FeatureFlagScope | ''>('');
  const [status, setStatus] = useState<StatusFilter>('Active');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [prefill, setPrefill] = useState<FlagPrefill | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<FeatureFlagCatalogItem[]>([]);

  const pageSize = 20;

  useEffect(() => {
    let cancelled = false;
    // Mesmo padrao de busca de dados das demais telas; ver FeatureFlagDetailPage.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLoading(true);
    setError(null);
    listFeatureFlags({
      page,
      pageSize,
      scope: scope || undefined,
      status: status || undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((err) => !cancelled && setError(describeError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [page, scope, status, reloadTick]);

  // Declarative catalog (flags known to the code) crossed with this env's state.
  useEffect(() => {
    let cancelled = false;
    getFeatureFlagCatalog()
      .then((items) => !cancelled && setCatalog(items))
      .catch(() => !cancelled && setCatalog([]));
    return () => { cancelled = true; };
  }, [reloadTick]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  // Kill switches pela key do catálogo. Sem catálogo, nenhuma linha é marcada e a API
  // continua recusando o arquivamento com um motivo que a tela traduz.
  const killSwitchKeys = useMemo(
    () => new Set(catalog.filter((c) => c.isKillSwitch).map((c) => c.key)),
    [catalog],
  );

  function configureFromCatalog(item: FeatureFlagCatalogItem) {
    setPrefill({
      key: item.key,
      displayName: item.displayName,
      description: item.description,
      scope: item.scope,
      defaultEnabled: item.defaultEnabled,
      defaultRolloutPercentage: item.defaultRolloutPercentage,
      isKillSwitch: item.isKillSwitch,
    });
    setShowCreate(true);
  }

  async function handleArchive(key: string) {
    if (!confirm(`Arquivar a flag "${key}"? Avaliações retornam false.`)) return;
    setArchiveError(null);
    try {
      await archiveFeatureFlag(key);
      setReloadTick((t) => t + 1);
    } catch (err) {
      setArchiveError(describeFeatureFlagError(err));
    }
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <button
          type="button"
          className="btn"
          onClick={() => { setPrefill(null); setShowCreate(true); }}
        >
          + Criar flag
        </button>
      </div>

      {catalog.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ marginTop: 0, fontSize: 15 }}>Disponíveis para configuração</h2>
          <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
            Flags conhecidas pelo código. Configure neste ambiente sem digitar a chave.
          </p>
          <div className="table-scroll">
            <CardTable>
              <THead>
                <Tr><Th>Key</Th><Th>Nome</Th><Th>Escopo</Th><Th>Audiência</Th><Th>Estado</Th><Th></Th></Tr>
              </THead>
              <TBody>
                {catalog.map((c) => (
                  <Tr key={c.key}>
                    <Td heading style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                      <span>{c.key}</span>
                      {c.isKillSwitch && <KillSwitchBadge />}
                    </Td>
                    <Td label="Nome">{c.displayName}</Td>
                    <Td label="Escopo"><span className="badge">{c.scope}</span></Td>
                    <Td label="Audiência" className="muted">{c.audience}</Td>
                    <Td label="Estado">
                      {c.configured
                        ? <span className="badge badge-success">Configurada</span>
                        : c.archived
                          ? <span className="badge">Arquivada</span>
                          : <span className="badge">Não configurada</span>}
                    </Td>
                    <Td action style={{ whiteSpace: 'nowrap' }}>
                      {c.configured || c.archived
                        ? <Link to={`/feature-flags/${encodeURIComponent(c.key)}`}>Detalhe →</Link>
                        : (
                          <button type="button" className="btn" onClick={() => configureFromCatalog(c)}
                            style={{ padding: '4px 12px', fontSize: 13 }}>
                            Configurar
                          </button>
                        )}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </CardTable>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="grid-2">
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="ff-scope">Escopo</label>
            <select
              id="ff-scope"
              value={scope}
              onChange={(e) => {
                setScope(e.target.value as FeatureFlagScope | '');
                setPage(1);
              }}
            >
              {SCOPE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s || 'Todos'}</option>
              ))}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="ff-status">Status</label>
            <select
              id="ff-status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as StatusFilter);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s === '' ? 'Todos' : (s === 'Active' ? 'Ativos' : 'Arquivados')}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {archiveError && <div className="error-banner">{archiveError}</div>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-scroll">
          <CardTable>
            <THead>
              <Tr>
                <Th>Key</Th>
                <Th>Nome</Th>
                <Th>Escopo</Th>
                <Th>Default</Th>
                <Th>Rollout</Th>
                <Th>Overrides</Th>
                <Th>Status</Th>
                <Th></Th>
              </Tr>
            </THead>
            <TBody>
              {loading && (
                <Tr><Td colSpan={8} style={{ textAlign: 'center', padding: 24 }} className="muted">Carregando…</Td></Tr>
              )}
              {!loading && items.length === 0 && (
                <Tr><Td colSpan={8} style={{ textAlign: 'center', padding: 32 }} className="muted">Nenhuma flag encontrada.</Td></Tr>
              )}
              {!loading && items.map((f) => (
                <Tr key={f.id}>
                  <Td heading style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    <span>{f.key}</span>
                    {killSwitchKeys.has(f.key) && <KillSwitchBadge />}
                  </Td>
                  <Td label="Nome">{f.displayName}</Td>
                  <Td label="Escopo"><span className="badge">{f.scope}</span></Td>
                  <Td label="Default">{f.defaultEnabled ? 'on' : 'off'}</Td>
                  <Td label="Rollout">{f.defaultRolloutPercentage ?? '—'}{f.defaultRolloutPercentage !== null ? '%' : ''}</Td>
                  <Td label="Overrides">{f.tenantOverrideCount}</Td>
                  <Td label="Status">{f.archivedAt ? <span className="badge">Arquivado</span> : <span className="badge badge-success">Ativo</span>}</Td>
                  <Td action style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`/feature-flags/${encodeURIComponent(f.key)}`}>Detalhe →</Link>
                    {!f.archivedAt && !killSwitchKeys.has(f.key) && (
                      <button
                        type="button"
                        onClick={() => handleArchive(f.key)}
                        style={{ marginLeft: 12, background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: 13 }}
                      >
                        Arquivar
                      </button>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </CardTable>
        </div>
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
          Anterior
        </button>
        <span className="muted">Página {page} de {totalPages} · {total} flags</span>
        <button className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
          Próxima
        </button>
      </div>

      {showCreate && (
        <FeatureFlagCreateModal
          prefill={prefill ?? undefined}
          onClose={() => { setShowCreate(false); setPrefill(null); }}
          onCreated={() => {
            setShowCreate(false);
            setPrefill(null);
            setReloadTick((t) => t + 1);
          }}
        />
      )}
    </div>
  );
}
